import { injectable } from 'inversify';
import { Observable, Subject } from 'rxjs';

import { ClientError } from '../utils/request.utils';
import { EnforcementCopy, EnforcementCta, enforcementCopy } from './enforcement-copy';

/**
 * Normalized enforcement / billing error shape that drives the
 * `<EnforcementErrorModal/>`. Source can be either:
 *
 *  - The legacy `enforcement_error` JSON envelope returned on HTTP 429
 *    by `/v1/api/servers/resolve` and emitted over the WS for post-upload
 *    rejection (`dataType: enforcement_error`).
 *  - The RFC 9457 `application/problem+json` envelope returned on HTTP
 *    402 (`code: insufficient_credits`) by the Developer-API surface.
 *
 * Both shapes are flattened here so the modal renders one consistent UI.
 */
export interface EnforcementErrorDetails {
    httpStatus: number;
    reason?: string;
    title: string;
    userMessage: string;
    currentValue?: number;
    limitValue?: number;
    currentTier?: string;
    upgradeRequired: boolean;
    subscribeUrl?: string;
    topupUrl?: string;
    matchedPattern?: string;
    /** Seconds until the limit lifts, when the server said (code 6.2 item 4). */
    retryAfterSeconds?: number;
    /**
     * Code 6 (ENFORCEMENT-POLICY.md §6.5): the words for a 429 envelope, built client-side from
     * `reason` / tier / counters only — never the server's `userMessage`, which carries the row
     * description and raw counters. Null for the 402 problem+json surface, which keeps its own text.
     */
    copy: EnforcementCopy | null;
    /** True when the identity is an IP (no login): the modal offers "Log in". */
    isAnonymous: boolean;
    ctas: EnforcementCta[];
}

interface LegacyEnforcementBody {
    error?: boolean;
    status?: number;
    dataType?: string;
    userMessage?: string;
    reason?: string;
    currentTier?: string;
    currentValue?: number;
    limitValue?: number;
    upgradeRequired?: boolean;
    matchedPattern?: string;
    suggestedTier?: string;
    retryAfterSeconds?: number;
    blockedUntil?: string;
}

interface ProblemJsonBody {
    type?: string;
    title?: string;
    status?: number;
    detail?: string;
    code?: string;
    reason?: string;
    currentBalance?: number;
    subscriptionBalance?: number;
    paygBalance?: number;
    estimatedCost?: number;
    subscribeUrl?: string;
    topupUrl?: string;
    userMessage?: string;
    currentTier?: string;
    currentValue?: number;
    limitValue?: number;
    upgradeRequired?: boolean;
    matchedPattern?: string;
}

/**
 * Global stream of enforcement/billing rejections. The
 * `<EnforcementErrorModal/>` mounted in `_app.tsx` subscribes to `event$`
 * and shows the modal whenever a new event arrives. Any service that
 * encounters a 429 `enforcement_error` or 402 `insufficient_credits`
 * (HTTP body or WS payload) calls `show(details)` and immediately
 * propagates the error to its caller — no retry.
 */
@injectable()
export class EnforcementErrorService {
    /**
     * Code 6 (b): ONE modal per batch. When N cards fail with the same limit within a few seconds
     * the modal opens once; every card still carries its own one-line message.
     */
    static readonly DEDUPE_WINDOW_MS = 10_000;

    private readonly subject = new Subject<EnforcementErrorDetails>();
    private lastShownKey: string | null = null;
    private lastShownAtMs = 0;
    /** Injectable clock (tests). */
    now: () => number = () => Date.now();

    get event$(): Observable<EnforcementErrorDetails> {
        return this.subject.asObservable();
    }

    /**
     * Open the modal — unless the same rejection (reason + limit + status) was shown within
     * `DEDUPE_WINDOW_MS`, in which case the batch has already heard it. Returns whether it emitted.
     */
    show(details: EnforcementErrorDetails): boolean {
        const key = EnforcementErrorService.dedupeKeyOf(details);
        const at = this.now();
        if (this.lastShownKey === key && at - this.lastShownAtMs < EnforcementErrorService.DEDUPE_WINDOW_MS) {
            return false;
        }
        this.lastShownKey = key;
        this.lastShownAtMs = at;
        this.subject.next(details);
        return true;
    }

    /** Forget the dedupe memory (a dismissed modal may be re-shown by the next distinct batch). */
    resetDedupe(): void {
        this.lastShownKey = null;
        this.lastShownAtMs = 0;
    }

    static dedupeKeyOf(details: EnforcementErrorDetails): string {
        return `${details.httpStatus}|${details.reason ?? ''}|${details.limitValue ?? ''}`;
    }

    /**
     * Detect whether an arbitrary error is an enforcement or
     * insufficient-credits rejection that the global modal can present.
     * Recognizes both the legacy `enforcement_error` envelope (429) and
     * the RFC 9457 `insufficient_credits` envelope (402).
     */
    static isEnforcementError(err: unknown): boolean {
        if (!err || typeof err !== 'object') return false;
        const status = (err as { status?: number }).status;
        if (status === 429 || status === 402) {
            const body = (err as { response?: unknown }).response;
            return EnforcementErrorService.bodyLooksLikeEnforcement(body);
        }
        return false;
    }

    private static bodyLooksLikeEnforcement(body: unknown): boolean {
        if (!body || typeof body !== 'object') return false;
        const obj = body as LegacyEnforcementBody & ProblemJsonBody;
        return obj.dataType === 'enforcement_error'
            || obj.code === 'insufficient_credits';
    }

    /**
     * Extract a normalized `EnforcementErrorDetails` from any error or
     * raw body. Tolerates both envelopes; falls back to a generic
     * message when fields are sparse.
     */
    static extractDetails(err: unknown): EnforcementErrorDetails {
        let status = 429;
        let body: LegacyEnforcementBody & ProblemJsonBody = {};
        if (err && typeof err === 'object') {
            status = (err as { status?: number }).status ?? 429;
            const responseField = (err as { response?: unknown }).response;
            if (responseField && typeof responseField === 'object') {
                body = responseField as typeof body;
            } else if (typeof responseField === 'string') {
                body = EnforcementErrorService.safeJsonParse(responseField);
            } else if (!(err as ClientError).response
                && typeof (err as { message?: string }).message === 'string') {
                body = EnforcementErrorService.safeJsonParse(
                    (err as { message: string }).message,
                );
            }
        }

        return EnforcementErrorService.fromBody(body, status);
    }

    /**
     * Build details directly from a parsed body (used by the WS
     * post-upload `enforcement_error` payload, which already arrives
     * parsed). `httpStatus` defaults to 429 — the WS path is always a
     * 429-equivalent enforcement rejection.
     */
    static fromBody(
        body: LegacyEnforcementBody & ProblemJsonBody,
        httpStatus = 429,
        nowMs?: number,
    ): EnforcementErrorDetails {
        const reason = body.reason
            || (body.code === 'insufficient_credits' ? 'insufficient_credits' : undefined);
        // The 429 `enforcement_error` envelope gets client-built copy (code 6: the server's
        // `userMessage` names the row and the bucket). The 402 problem+json surface keeps its text.
        const isLimitEnvelope = httpStatus !== 402 && body.code !== 'insufficient_credits';
        const copy = isLimitEnvelope
            ? enforcementCopy({
                reason,
                currentTier: body.currentTier,
                currentValue: body.currentValue,
                limitValue: body.limitValue,
                upgradeRequired: body.upgradeRequired,
                suggestedTier: body.suggestedTier,
                retryAfterSeconds: body.retryAfterSeconds,
                blockedUntil: body.blockedUntil,
            }, { nowMs })
            : null;
        const title = copy?.title
            || body.title
            || EnforcementErrorService.titleFromReason(reason, httpStatus);
        const userMessage = copy?.body
            || body.userMessage
            || body.detail
            || EnforcementErrorService.fallbackMessage(reason, body);
        const upgradeRequired = body.upgradeRequired
            ?? (httpStatus === 402);
        return {
            httpStatus,
            reason,
            title,
            userMessage,
            currentValue: body.currentValue,
            limitValue: body.limitValue,
            currentTier: body.currentTier,
            upgradeRequired,
            subscribeUrl: body.subscribeUrl,
            topupUrl: body.topupUrl,
            matchedPattern: body.matchedPattern,
            retryAfterSeconds: body.retryAfterSeconds,
            copy,
            isAnonymous: copy?.audience === 'connection',
            ctas: copy?.ctas ?? [],
        };
    }

    /**
     * The card's one-line message for an `enforcement_error` frame (code 6 (c)): the same fact as
     * the modal in one sentence, "Resets at HH:MM" when the reset is known, never a pattern name.
     */
    static cardLineFromBody(body: LegacyEnforcementBody & ProblemJsonBody, nowMs?: number): string {
        return EnforcementErrorService.fromBody(body, 429, nowMs).copy?.cardLine
            || 'Usage limit reached. Try again later.';
    }

    /** 402 / Developer-API reasons only; every 429 limit envelope is worded by `enforcementCopy`. */
    private static titleFromReason(reason: string | undefined, status: number): string {
        if (status === 402) return 'Insufficient credits';
        switch (reason) {
            case 'no_active_subscription':
                return 'No active API subscription';
            case 'insufficient_balance':
                return 'Insufficient credit balance';
            default:
                return "You've hit a limit";
        }
    }

    private static fallbackMessage(
        reason: string | undefined,
        body: LegacyEnforcementBody & ProblemJsonBody,
    ): string {
        if (reason === 'no_active_subscription') {
            return 'Your API subscription is not active. Subscribe to use the API.';
        }
        return 'Your request was rejected by usage limits. Try again later or upgrade your plan.';
    }

    private static safeJsonParse(maybeJson: string): LegacyEnforcementBody & ProblemJsonBody {
        try {
            return JSON.parse(maybeJson) as LegacyEnforcementBody & ProblemJsonBody;
        } catch {
            return {};
        }
    }
}
